import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import {
  Save, User, Briefcase, Mail, Phone, Calendar,
  Shield, AlertCircle, Building2, Loader2, Plus, Trash2, Users, Edit2
} from 'lucide-react';

const ROLE_TYPES = [
  { value: 'director', label: 'Director' },
  { value: 'executive', label: 'Executive' },
  { value: 'other', label: 'Other' }
];

const TITLE_SUGGESTIONS = [
  'Managing Director',
  'Chief Executive Officer (CEO)',
  'Chief Financial Officer (CFO)',
  'Chief Operating Officer (COO)',
  'Chief Sustainability Officer (CSO)',
  'Chief Risk Officer (CRO)',
  'Company Secretary',
  'Non-Executive Director',
  'Chair of the Board'
];

const GovernancePage = () => {
  const navigate = useNavigate();
  const { currentOrg, user, userData } = useOutletContext();
  const currentUserId = userData?.id;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [governanceId, setGovernanceId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'personnel'

  // Personnel state
  const [personnel, setPersonnel] = useState([]);
  const [editingPerson, setEditingPerson] = useState(null);
  const [showPersonnelForm, setShowPersonnelForm] = useState(false);
  const [personnelForm, setPersonnelForm] = useState({
    full_name: '',
    title: '',
    role_type: 'other',
    email: '',
    phone: '',
    appointed_date: '',
    responsibilities: '',
    is_authorised_signatory: false
  });

  const [formData, setFormData] = useState({
    // Responsible Officer Details
    responsible_officer_name: '',
    responsible_officer_title: '',
    responsible_officer_email: '',
    responsible_officer_phone: '',
    responsible_officer_appointed_date: '',

    // Board Oversight
    board_oversight_frequency: 'quarterly',
    board_committee_name: '',

    // Climate Governance Statement
    governance_statement: '',

    // Last Updated
    governance_last_reviewed: ''
  });

  // Fetch governance data
  const fetchGovernanceData = useCallback(async () => {
    if (!currentOrg) return;
    try {
      setLoading(true);

      // Fetch existing governance data
      const { data: governanceData, error } = await supabase
        .from('governance')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching governance:', error);
      }

      if (governanceData) {
        setGovernanceId(governanceData.id);
        setFormData({
          responsible_officer_name: governanceData.responsible_officer_name || '',
          responsible_officer_title: governanceData.responsible_officer_title || '',
          responsible_officer_email: governanceData.responsible_officer_email || '',
          responsible_officer_phone: governanceData.responsible_officer_phone || '',
          responsible_officer_appointed_date: governanceData.responsible_officer_appointed_date
            ? new Date(governanceData.responsible_officer_appointed_date).toISOString().split('T')[0]
            : '',
          board_oversight_frequency: governanceData.board_oversight_frequency || 'quarterly',
          board_committee_name: governanceData.board_committee_name || '',
          governance_statement: governanceData.governance_statement || '',
          governance_last_reviewed: governanceData.governance_last_reviewed
            ? new Date(governanceData.governance_last_reviewed).toISOString().split('T')[0]
            : ''
        });
      }

      // Fetch personnel
      await fetchPersonnel(currentOrg.id);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load governance data');
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  // Fetch personnel
  const fetchPersonnel = async (orgId) => {
    try {
      const { data, error } = await supabase
        .from('governance_personnel')
        .select('*')
        .eq('organisation_id', orgId)
        .eq('is_active', true)
        .order('role_type', { ascending: true });

      if (error) {
        console.error('Error fetching personnel:', error);
        // Table might not exist yet
        return;
      }

      setPersonnel(data || []);
    } catch (error) {
      console.error('Error fetching personnel:', error);
    }
  };

  // Add or update personnel
  const handleSavePersonnel = async () => {
    if (!currentOrg || !personnelForm.full_name || !personnelForm.title) {
      toast.error('Name and Title are required');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        organisation_id: currentOrg.id,
        full_name: personnelForm.full_name,
        title: personnelForm.title,
        role_type: personnelForm.role_type,
        email: personnelForm.email || null,
        phone: personnelForm.phone || null,
        appointed_date: personnelForm.appointed_date || null,
        responsibilities: personnelForm.responsibilities || null,
        is_authorised_signatory: personnelForm.is_authorised_signatory,
        created_by: currentUserId,
        updated_at: new Date().toISOString()
      };

      if (editingPerson) {
        // Update existing
        const { error } = await supabase
          .from('governance_personnel')
          .update(payload)
          .eq('id', editingPerson.id);

        if (error) throw error;
        toast.success('Personnel updated successfully');
      } else {
        // Insert new
        const { error } = await supabase
          .from('governance_personnel')
          .insert(payload);

        if (error) throw error;
        toast.success('Personnel added successfully');
      }

      // Reset form and refresh list
      setPersonnelForm({
        full_name: '',
        title: '',
        role_type: 'other',
        email: '',
        phone: '',
        appointed_date: '',
        responsibilities: '',
        is_authorised_signatory: false
      });
      setEditingPerson(null);
      setShowPersonnelForm(false);
      await fetchPersonnel(currentOrg.id);

    } catch (error) {
      console.error('Error saving personnel:', error);
      toast.error('Failed to save personnel');
    } finally {
      setSaving(false);
    }
  };

  // Delete personnel
  const handleDeletePersonnel = async (person) => {
    if (person.role_type === 'responsible_officer') {
      toast.error('Cannot delete the Responsible Officer. You can only edit this role.');
      return;
    }

    if (!confirm(`Are you sure you want to remove ${person.full_name}?`)) return;

    try {
      const { error } = await supabase
        .from('governance_personnel')
        .update({ is_active: false })
        .eq('id', person.id);

      if (error) throw error;

      toast.success('Personnel removed');
      await fetchPersonnel(currentOrg.id);
    } catch (error) {
      console.error('Error deleting personnel:', error);
      toast.error('Failed to remove personnel');
    }
  };

  // Edit personnel
  const handleEditPersonnel = (person) => {
    setPersonnelForm({
      full_name: person.full_name || '',
      title: person.title || '',
      role_type: person.role_type || 'other',
      email: person.email || '',
      phone: person.phone || '',
      appointed_date: person.appointed_date
        ? new Date(person.appointed_date).toISOString().split('T')[0]
        : '',
      responsibilities: person.responsibilities || '',
      is_authorised_signatory: person.is_authorised_signatory || false
    });
    setEditingPerson(person);
    setShowPersonnelForm(true);
  };

  useEffect(() => {
    fetchGovernanceData();
  }, [fetchGovernanceData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentOrg) {
      toast.error('Organisation not loaded');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        organisation_id: currentOrg.id,
        responsible_officer_name: formData.responsible_officer_name || null,
        responsible_officer_title: formData.responsible_officer_title || null,
        responsible_officer_email: formData.responsible_officer_email || null,
        responsible_officer_phone: formData.responsible_officer_phone || null,
        responsible_officer_appointed_date: formData.responsible_officer_appointed_date || null,
        board_oversight_frequency: formData.board_oversight_frequency,
        board_committee_name: formData.board_committee_name || null,
        governance_statement: formData.governance_statement || null,
        governance_last_reviewed: formData.governance_last_reviewed || null,
        updated_at: new Date().toISOString()
      };

      if (governanceId) {
        // Update existing record
        const { error } = await supabase
          .from('governance')
          .update(payload)
          .eq('id', governanceId);

        if (error) throw error;

        // Log to audit
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          user_id: currentUserId,
          organisation_id: currentOrg.id,
          resource_type: 'governance',
          resource_id: governanceId,
          action: 'update',
          description: 'Updated governance information'
        });
      } else {
        // Create new record
        const newId = crypto.randomUUID();
        const { error } = await supabase
          .from('governance')
          .insert({
            id: newId,
            ...payload,
            created_at: new Date().toISOString()
          });

        if (error) throw error;

        setGovernanceId(newId);

        // Log to audit
        await supabase.from('audit_logs').insert({
          id: crypto.randomUUID(),
          user_id: currentUserId,
          organisation_id: currentOrg.id,
          resource_type: 'governance',
          resource_id: newId,
          action: 'create',
          description: 'Created governance information'
        });
      }

      toast.success('Governance information saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save governance information');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#003B5C] mx-auto mb-4" />
          <p className="text-slate-600">Loading governance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]" data-testid="governance-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Building2 className="w-7 h-7 mr-3 text-[#74A57F]" />
              Governance & Oversight
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • AASB S2 requires disclosure of climate governance structures
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 mb-6">
        <div className="inline-flex space-x-1 bg-slate-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${activeTab === 'overview'
              ? 'bg-white text-[#003B5C] shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
              }`}
          >
            <Shield className="w-4 h-4 mr-2" />
            Overview & Policies
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('personnel')}
            className={`py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${activeTab === 'personnel'
              ? 'bg-white text-[#003B5C] shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
              }`}
          >
            <Users className="w-4 h-4 mr-2" />
            Personnel ({personnel.length})
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' ? (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Responsible Officer Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#003B5C]/10 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-[#003B5C]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#003B5C]">Responsible Officer</h2>
                  <p className="text-sm text-slate-500">Person accountable for ESG/climate matters</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="officer_name">Full Name *</Label>
                  <Input
                    id="officer_name"
                    value={formData.responsible_officer_name}
                    onChange={(e) => updateField('responsible_officer_name', e.target.value)}
                    placeholder="e.g., Jane Smith"
                    className="mt-1"
                    data-testid="officer-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="officer_title">Position/Title *</Label>
                  <Input
                    id="officer_title"
                    value={formData.responsible_officer_title}
                    onChange={(e) => updateField('responsible_officer_title', e.target.value)}
                    placeholder="e.g., Chief Sustainability Officer"
                    className="mt-1"
                    data-testid="officer-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="officer_email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="officer_email"
                      type="email"
                      value={formData.responsible_officer_email}
                      onChange={(e) => updateField('responsible_officer_email', e.target.value)}
                      placeholder="jane@company.com.au"
                      className="pl-10"
                      data-testid="officer-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="officer_phone">Phone Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="officer_phone"
                      value={formData.responsible_officer_phone}
                      onChange={(e) => updateField('responsible_officer_phone', e.target.value)}
                      placeholder="+61 4XX XXX XXX"
                      className="pl-10"
                      data-testid="officer-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="officer_appointed">Appointment Date</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="officer_appointed"
                      type="date"
                      value={formData.responsible_officer_appointed_date}
                      onChange={(e) => updateField('responsible_officer_appointed_date', e.target.value)}
                      className="pl-10"
                      data-testid="officer-appointed-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Board Oversight Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#003B5C]">Board Oversight</h2>
                  <p className="text-sm text-slate-500">How does the board oversee climate matters?</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="oversight_frequency">Review Frequency *</Label>
                  <Select
                    value={formData.board_oversight_frequency}
                    onValueChange={(val) => updateField('board_oversight_frequency', val)}
                  >
                    <SelectTrigger className="mt-1" data-testid="oversight-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="biannually">Bi-annually</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                      <SelectItem value="adhoc">Ad-hoc / As needed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="committee_name">Committee Name (if applicable)</Label>
                  <Input
                    id="committee_name"
                    value={formData.board_committee_name}
                    onChange={(e) => updateField('board_committee_name', e.target.value)}
                    placeholder="e.g., Sustainability Committee"
                    className="mt-1"
                    data-testid="committee-name-input"
                  />
                </div>
              </div>
            </div>

            {/* Governance Statement Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#74A57F]/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#74A57F]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[#003B5C]">Climate Governance Statement</h2>
                  <p className="text-sm text-slate-500">Describe your organisation's climate governance approach</p>
                </div>
              </div>

              <div>
                <Label htmlFor="governance_statement">Statement</Label>
                <Textarea
                  id="governance_statement"
                  value={formData.governance_statement}
                  onChange={(e) => updateField('governance_statement', e.target.value)}
                  placeholder="Describe how your organisation governs climate-related risks and opportunities. Include information about roles, responsibilities, and decision-making processes..."
                  className="mt-1 min-h-[150px]"
                  data-testid="governance-statement-textarea"
                />
                <p className="text-xs text-slate-400 mt-2">
                  This statement will be included in your AASB S2 Climate Disclosure Report.
                </p>
              </div>

              <div className="mt-6">
                <Label htmlFor="last_reviewed">Last Reviewed Date</Label>
                <Input
                  id="last_reviewed"
                  type="date"
                  value={formData.governance_last_reviewed}
                  onChange={(e) => updateField('governance_last_reviewed', e.target.value)}
                  className="mt-1 w-48"
                  data-testid="last-reviewed-input"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">AASB S2 Governance Disclosure</p>
                <p className="text-sm text-blue-700 mt-1">
                  Under AASB S2, entities must disclose how governance bodies oversee climate-related risks and opportunities,
                  including the identity of the responsible person or committee and how they are informed about climate matters.
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                data-testid="save-governance-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Governance Info
                  </>
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Personnel Tab */}
            {/* Add Personnel Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-[#003B5C]">Governance Personnel</h2>
                <p className="text-sm text-slate-500">Manage directors, executives, and key governance personnel</p>
              </div>
              <Button
                onClick={() => {
                  setEditingPerson(null);
                  setPersonnelForm({
                    full_name: '',
                    title: '',
                    role_type: 'other',
                    email: '',
                    phone: '',
                    appointed_date: '',
                    responsibilities: '',
                    is_authorised_signatory: false
                  });
                  setShowPersonnelForm(true);
                }}
                className="bg-[#003B5C] hover:bg-[#002A42] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Personnel
              </Button>
            </div>

            {/* Personnel Form Modal/Section */}
            {showPersonnelForm && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-[#003B5C]">
                    {editingPerson ? 'Edit Personnel' : 'Add New Personnel'}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowPersonnelForm(false);
                      setEditingPerson(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={personnelForm.full_name}
                      onChange={(e) => setPersonnelForm(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="e.g., John Smith"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Title/Position *</Label>
                    <Input
                      value={personnelForm.title}
                      onChange={(e) => setPersonnelForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Managing Director"
                      className="mt-1"
                      list="title-suggestions"
                    />
                    <datalist id="title-suggestions">
                      {TITLE_SUGGESTIONS.map(t => (
                        <option key={t} value={t} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <Label>Role Type</Label>
                    <Select
                      value={personnelForm.role_type}
                      onValueChange={(v) => setPersonnelForm(prev => ({ ...prev, role_type: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_TYPES.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Appointed Date</Label>
                    <Input
                      type="date"
                      value={personnelForm.appointed_date}
                      onChange={(e) => setPersonnelForm(prev => ({ ...prev, appointed_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={personnelForm.email}
                      onChange={(e) => setPersonnelForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john.smith@company.com"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={personnelForm.phone}
                      onChange={(e) => setPersonnelForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+61 4XX XXX XXX"
                      className="mt-1"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Sustainability Responsibilities</Label>
                    <Textarea
                      value={personnelForm.responsibilities}
                      onChange={(e) => setPersonnelForm(prev => ({ ...prev, responsibilities: e.target.value }))}
                      placeholder="Describe this person's sustainability-related responsibilities..."
                      className="mt-1 min-h-[80px]"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={personnelForm.is_authorised_signatory}
                        onChange={(e) => setPersonnelForm(prev => ({ ...prev, is_authorised_signatory: e.target.checked }))}
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm text-slate-700">Can sign sustainability reports (Authorised Signatory)</span>
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={handleSavePersonnel}
                    disabled={saving}
                    className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingPerson ? 'Update Personnel' : 'Add Personnel'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Personnel List */}
            <div className="space-y-4">
              {/* Responsible Officer (from main governance form) */}
              {formData.responsible_officer_name && (
                <div className="bg-white rounded-xl border-2 border-[#74A57F] p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#74A57F]/20 rounded-full flex items-center justify-center">
                        <Shield className="w-6 h-6 text-[#74A57F]" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-[#003B5C]">{formData.responsible_officer_name}</h3>
                          <span className="text-xs bg-[#74A57F] text-white px-2 py-0.5 rounded">Responsible Officer</span>
                        </div>
                        <p className="text-slate-600">{formData.responsible_officer_title}</p>
                        {formData.responsible_officer_email && (
                          <p className="text-sm text-slate-500 mt-1">{formData.responsible_officer_email}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab('overview')}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">
                    The Responsible Officer is required and cannot be deleted. Edit details in the Overview tab.
                  </p>
                </div>
              )}

              {/* Other Personnel */}
              {personnel.map(person => (
                <div key={person.id} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-slate-500" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-[#003B5C]">{person.full_name}</h3>
                          {person.is_authorised_signatory && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Signatory</span>
                          )}
                        </div>
                        <p className="text-slate-600">{person.title}</p>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-slate-500">
                          {person.email && <span>{person.email}</span>}
                          {person.role_type && (
                            <span className="capitalize">{person.role_type.replace('_', ' ')}</span>
                          )}
                        </div>
                        {person.responsibilities && (
                          <p className="text-sm text-slate-500 mt-2">{person.responsibilities}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditPersonnel(person)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {person.role_type !== 'responsible_officer' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePersonnel(person)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {personnel.length === 0 && !formData.responsible_officer_name && (
                <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 p-8 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No personnel added yet</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Add directors, executives, and key personnel responsible for governance
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovernancePage;
